import { useRef, useLayoutEffect, useState, useEffect } from 'react';

/**
 * Metin bloğunu 40mm yüksekliğe sığdırmak için font boyutunu otomatik ayarlar
 * - Mevcut font boyutunu korur (4.6pt)
 * - 40mm yüksekliği aşarsa font küçülür
 * - Genişlik sabit 176px
 * - Minimum font boyutu: 2pt
 */
export function useTextFit() {
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState(4.6); // pt
  const MIN_FONT_SIZE = 2; // pt
  const MAX_FONT_SIZE = 4.6; // pt - mevcut font boyutu
  const MAX_HEIGHT_MM = 40; // mm
  const MAX_HEIGHT_PX = MAX_HEIGHT_MM * 3.7795275591; // mm to px conversion
  const FIXED_WIDTH_PX = 176; // px - sabit genişlik

  // Belirli font boyutunda sığıyor mu kontrol et
  const fitsAt = (sizePt) => {
    const container = containerRef.current;
    if (!container) return true;

    // Geçici olarak font boyutunu ayarla
    container.style.setProperty('--temp-font-size', `${sizePt}pt`);
    
    // Tüm metin elementlerini kontrol et
    const textElements = container.querySelectorAll('[data-text-fit]');
    let fits = true;

    textElements.forEach(element => {
      // Geçici font boyutunu uygula
      element.style.fontSize = `${sizePt}pt`;
    });

    // Container'ın toplam yüksekliğini kontrol et
    if (container.scrollHeight > MAX_HEIGHT_PX) {
      fits = false;
    }

    return fits;
  };

  // En büyük sığan font boyutunu bul
  const recalculateFontSize = () => {
    if (!containerRef.current) return;

    // İlk deneme: mevcut font boyutu sığıyor mu?
    if (fitsAt(MAX_FONT_SIZE)) {
      setFontSize(MAX_FONT_SIZE);
      return;
    }

    // Binary search ile en büyük sığan font boyutunu bul
    let low = MIN_FONT_SIZE;
    let high = MAX_FONT_SIZE;
    let best = MIN_FONT_SIZE;

    for (let i = 0; i < 20; i++) {
      const mid = (low + high) / 2;
      
      if (fitsAt(mid)) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
      
      if (high - low < 0.1) break; // 0.1pt hassasiyet yeterli
    }

    const finalSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, Math.round(best * 10) / 10));
    setFontSize(finalSize);
  };

  // İçerik değiştiğinde yeniden hesapla
  useLayoutEffect(() => {
    recalculateFontSize();
  });

  // Resize observer ile kutu boyutu değişikliklerini takip et
  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId;
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        recalculateFontSize();
      }, 16); // 60fps
    });

    resizeObserver.observe(containerRef.current);

    // Window resize eventi
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        recalculateFontSize();
      }, 16);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    containerRef,
    fontSize: `${fontSize}pt`,
    recalculateFontSize
  };
}
